import { apiClient } from '@/utils/api-client'

const API_INTERNAL_BASE = '/projects'

/**
 * Segment service for managing segments and context membership.
 */
class SegmentService {
    /**
     * Create a new segment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {Object} form - Segment payload
     * @returns {Promise<Object>} Create response
     */
    async create(projectKey, environmentKey, form) {
        return await apiClient
            .post(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/segments',
                form
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Remove a segment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} segmentUlid - Segment ULID
     * @returns {Promise<Object>} Remove response
     */
    async remove(projectKey, environmentKey, segmentUlid) {
        return await apiClient
            .delete(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/segments/' +
                    segmentUlid
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Get a segment with contexts.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} segmentUlid - Segment ULID
     * @returns {Promise<Object>} Segment response
     */
    async get(projectKey, environmentKey, segmentUlid) {
        return await apiClient
            .get(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/segments/' +
                    segmentUlid +
                    '?include=contexts'
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * List segments for an environment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @returns {Promise<Object>} Segment list response
     */
    async list(projectKey, environmentKey) {
        return await apiClient
            .get(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/segments'
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Lookup segments for search/autocomplete.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @returns {Promise<Object>} Lookup response
     */
    async lookup(projectKey, environmentKey) {
        return await apiClient
            .get(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/segments?lookup=true'
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Update a segment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} segmentUlid - Segment ULID
     * @param {Object} form - Update payload
     * @returns {Promise<Object>} Update response
     */
    async update(projectKey, environmentKey, segmentUlid, form) {
        return await apiClient
            .put(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/segments/' +
                    segmentUlid,
                form
            )
            .then((response) => {
                return response.data
            })
    }

    // ********************************************************************************************

    /**
     * Attach a context to a segment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} segmentUlid - Segment ULID
     * @param {string} contextUlid - Context ULID
     * @returns {Promise<Object>} Attach response
     */
    async attachContext(projectKey, environmentKey, segmentUlid, contextUlid) {
        return await apiClient
            .post(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/segments/' +
                    segmentUlid +
                    '/contexts/' +
                    contextUlid,
                []
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Detach a context from a segment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} segmentUlid - Segment ULID
     * @param {string} contextUlid - Context ULID
     * @returns {Promise<Object>} Detach response
     */
    async detachContext(projectKey, environmentKey, segmentUlid, contextUlid) {
        return await apiClient
            .delete(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/segments/' +
                    segmentUlid +
                    '/contexts/' +
                    contextUlid
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Attach a context identifier to a segment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} segmentUlid - Segment ULID
     * @param {string} identifier - Context identifier
     * @returns {Promise<Object>} Attach response
     */
    async attachContextByIdentifier(
        projectKey,
        environmentKey,
        segmentUlid,
        identifier
    ) {
        return await apiClient
            .post(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/segments/' +
                    segmentUlid +
                    '/contexts',
                { identifier }
            )
            .then((response) => {
                return response.data
            })
    }

    /**
     * Detach a context identifier from a segment.
     *
     * @param {string} projectKey - Project key
     * @param {string} environmentKey - Environment key
     * @param {string} segmentUlid - Segment ULID
     * @param {string} identifier - Context identifier
     * @returns {Promise<Object>} Detach response
     */
    async detachContextByIdentifier(
        projectKey,
        environmentKey,
        segmentUlid,
        identifier
    ) {
        return await apiClient
            .delete(
                API_INTERNAL_BASE +
                    '/' +
                    projectKey +
                    '/environments/' +
                    environmentKey +
                    '/segments/' +
                    segmentUlid +
                    '/contexts',
                {
                    data: { identifier },
                }
            )
            .then((response) => {
                return response.data
            })
    }
}

export default new SegmentService()
